import React, { useEffect, useState, useCallback } from 'react';

import { Paper } from '@material-ui/core';
import { clearReloadStatsState } from 'actions/continuousFeedback';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  Divider1,
  VerticalFlexContainer,
  VerticallyAlignedFlexContainer,
  ColoredCircularProgress,
  VerticalDivider,
  StyledGrid,
} from 'styles/common';

import { FEEDBACK_FORM_TYPE } from 'constants/ProjectConstants';
import { pushFeedbackParams } from 'constants/helper';
import { ContinuousFeedbackIcon } from 'constants/icons';
import { sendContinuousFeedbackEvents, sendViewedShared } from 'constants/mixpanelHelper';
import theme from 'constants/theme';
import { EVENT_SOURCES } from 'constants/trackingEvents';

import { getLandingPageFeedbackStats } from 'services/continuousFeedback';
import { showErrorSnackbar } from 'services/snackbar';

import { TextButtonHoverFilled, ButtonFilled, ButtonOutlined } from 'uiToolkit/Button';
import { Typography13, Typography40 } from 'uiToolkit/Typography';

import SectionHeader from './common/SectionHeader';

const NewFeedbackCard = ({ loading, count, label, color, onClick }) => (
  <TextButtonHoverFilled
    onClick={onClick}
    style={{ height: '152px', width: '120px' }}
    color={theme.palette.background.lightBlue}
  >
    <VerticalFlexContainer style={{ alignItems: 'center' }}>
      {loading ? <ColoredCircularProgress /> : <Typography40 color={color}>{count}</Typography40>}
      <Typography13 topGutter={2} color="primary">
        {label}
      </Typography13>
    </VerticalFlexContainer>
  </TextButtonHoverFilled>
);

const StatsContainer = styled(VerticallyAlignedFlexContainer)`
  justify-content: space-around;
  padding: 38px 16px;
  flex: 1;
`;

const StyledContainer = styled(Paper)`
  height: 40vh;
  min-height: 300px;
  max-height: 500px;
  display: flex;
  flex-direction: column;
`;

const Cards = {
  received: {
    label: 'Received Feedback',
    color: theme.palette.text.green,
  },
  requested: {
    label: 'Requested Feedback',
    color: theme.palette.text.orangeRed,
  },
  shared: {
    label: 'Shared Feedback',
    color: theme.palette.primary.main,
  },
};

const ContinuousFeedback = ({ reloadStats }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const getStatsData = () => {
    setLoading(true);
    getLandingPageFeedbackStats()
      .then((res) => {
        setStats(res);
      })
      .catch(() => dispatch(showErrorSnackbar('Oops! Something Went Wrong While Fetching Stats!')))
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getStatsData();
  }, []);

  useEffect(() => {
    if (reloadStats) {
      getStatsData();
      dispatch(clearReloadStatsState());
    }
  }, [reloadStats]);

  const handleSidePanelState = ({ formType, defaultTab }) => {
    let data = {
      history,
      formType,
      newForm: formType !== FEEDBACK_FORM_TYPE.LIST_FEEDBACKS,
      typeMapped: formType === FEEDBACK_FORM_TYPE.LIST_FEEDBACKS,
    };

    if (formType === FEEDBACK_FORM_TYPE.LIST_FEEDBACKS) {
      data = {
        ...data,
        defaultTab,
      };
    }
    pushFeedbackParams(data);
  };

  const handleRequestedFeedbackClick = useCallback(() => {
    handleSidePanelState({
      formType: FEEDBACK_FORM_TYPE.LIST_FEEDBACKS,
      defaultTab: 0,
    });
  }, [sendContinuousFeedbackEvents, handleSidePanelState]);

  const handleReceivedFeedbackClick = useCallback(() => {
    handleSidePanelState({
      formType: FEEDBACK_FORM_TYPE.SHOW_RECEIVED,
    });
  }, [sendContinuousFeedbackEvents, handleSidePanelState]);

  const handleSharedFeedbackClick = useCallback(() => {
    sendViewedShared({
      eventProperties: {
        source: EVENT_SOURCES.REVIEWS_PAGE,
      },
    });

    handleSidePanelState({
      formType: FEEDBACK_FORM_TYPE.SHOW_PROVIDED,
    });
  }, [sendContinuousFeedbackEvents, handleSidePanelState]);

  const handleRequestNewFeedbackClick = useCallback(() => {
    handleSidePanelState({ formType: FEEDBACK_FORM_TYPE.REQUESTED });
  }, [sendContinuousFeedbackEvents, handleSidePanelState]);

  const handleShareNewFeedbackClick = useCallback(() => {
    handleSidePanelState({ formType: FEEDBACK_FORM_TYPE.PROVIDED });
  }, [sendContinuousFeedbackEvents, handleSidePanelState]);

  return (
    <>
      <SectionHeader icon={<ContinuousFeedbackIcon />} title="Continuous Feedback" withInComponent={false} />
      <StyledContainer>
        <StatsContainer>
          <NewFeedbackCard
            loading={loading}
            count={stats?.receivedFeedbackCount}
            color={Cards.received.color}
            label={Cards.received.label}
            onClick={handleReceivedFeedbackClick}
          />
          <VerticalDivider height="60px" />
          <NewFeedbackCard
            loading={loading}
            count={stats?.requestedFeedbackCount}
            color={Cards.requested.color}
            label={Cards.requested.label}
            onClick={handleRequestedFeedbackClick}
          />
          <VerticalDivider height="60px" />
          <NewFeedbackCard
            loading={loading}
            count={stats?.sharedFeedbackCount}
            color={Cards.shared.color}
            label={Cards.shared.label}
            onClick={handleSharedFeedbackClick}
          />
        </StatsContainer>
        <Divider1 />
        <StyledGrid container alignItems="center" style={{ flex: 0, padding: '1rem 1.5rem' }}>
          <StyledGrid item xs={6} style={{ paddingRight: '1rem' }}>
            <ButtonOutlined
              borderColor="primary"
              style={{ width: '100%', height: '40px' }}
              onClick={handleRequestNewFeedbackClick}
            >
              <Typography13 color="primary"> Request </Typography13>
            </ButtonOutlined>
          </StyledGrid>
          <StyledGrid item xs={6} style={{ paddingLeft: '1rem' }}>
            <ButtonFilled style={{ width: '100%', height: '40px' }} onClick={handleShareNewFeedbackClick}>
              <Typography13 color="white"> Share </Typography13>
            </ButtonFilled>
          </StyledGrid>
        </StyledGrid>
      </StyledContainer>
    </>
  );
};

export default ContinuousFeedback;
